import { useEffect, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useAppInfoStore } from '../../store';

/** 브릿지 응답 대기 타임아웃 (ms) — 5초 초과 시 무응답으로 간주 */
const BRIDGE_TIMEOUT_MS = 5000;

export const useGetAppTypeByApp = () => {
  const { setAppType, setAppVersion, setBridgeStatus, setFirebaseToken } = useAppInfoStore(
    useShallow((state) => ({
      setAppType: state.actions.setAppType,
      setAppVersion: state.actions.setAppVersion,
      setBridgeStatus: state.actions.setBridgeStatus,
      setFirebaseToken: state.actions.setFirebaseToken,
    }))
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestAppType = (): void => {
    try {
      if (window.JSBridge) {
        window.JSBridge.getAppType();
      }

      if (window.webkit) {
        window.webkit.messageHandlers.getAppType?.postMessage(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAppTypeResult = async (
    appType?: string | null,
    appVersion?: string | null,
    _hasDeepLink?: string | boolean,
    token?: string | null
  ): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setAppType(appType ?? null);
    setAppVersion(appVersion ?? null);
    setFirebaseToken(token ?? null);
    setBridgeStatus('done');
  };

  useEffect(() => {
    window.onAppTypeResult = handleAppTypeResult;

    const hasBridge = !!window.JSBridge || !!window.webkit;

    // 브릿지가 없으면 브라우저 환경으로 즉시 확정
    if (!hasBridge) {
      setAppType('');
      setBridgeStatus('done');
      return () => {
        window.onAppTypeResult = null;
      };
    }

    // 브릿지가 있으면 단일 요청 후 응답 대기. 타임아웃까지 무응답이면 실패 처리 (재요청 없음)
    requestAppType();
    timerRef.current = setTimeout(() => {
      setBridgeStatus('failed');
    }, BRIDGE_TIMEOUT_MS);

    return () => {
      window.onAppTypeResult = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
};
